// material
import React, { useState, useEffect, useMemo } from "react";
import { FEATURE_FLAGS } from "src/config/featureFlags";
import {
  Container,
  Typography,
  Box,
  Tab,
  Tabs,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  LinearProgress,
} from "@material-ui/core";
import { Titulo } from "src/components/Titulo";
import {
  AssignmentOutlined,
  ReceiptOutlined,
  WarningAmberOutlined,
} from "@material-ui/icons";

// components
import Page from "../components/Page";
import TablaInicioCliente from "src/components/pagesCliente/TablaInicioCliente";
import TareasTerminadas from "src/components/pagesCliente/TareasTerminadas";
import FacturasPendientesPagoDialog from "src/components/pagesCliente/FacturasPendientesPagoDialog";
import { useClienteActual } from "src/hooks/useClienteActual";
import CardSkeleton from "src/components/cliente/loading/CardSkeleton";
import StatusBadge from "src/components/cliente/badges/StatusBadge";
import PaymentBadge from "src/components/cliente/badges/PaymentBadge";
import { cardStyles } from "src/components/cliente/styles/clientePortalStyles";

// RTK Query
import {
  useObtenerTareasClienteQuery,
  useObtenerFacturasPendientesPagoQuery,
  useObtenerFacturasImpagasQuery,
} from "src/services/accesoClientesApi";
import { makeStyles } from "@material-ui/styles";
import { formatAsCLP } from "src/utils/formatNumber";
// ----------------------------------------------------------------------
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && <React.Fragment>{children}</React.Fragment>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `full-width-tab-${index}`,
    "aria-controls": `full-width-tabpanel-${index}`,
  };
}

// ==================== ESTILOS ====================

const useStyles = makeStyles((theme) => ({
  metricCard: {
    ...cardStyles,
    height: "100%",
    cursor: "pointer",
    minHeight: 220,
    [theme.breakpoints.down("sm")]: {
      minHeight: 180,
    },
  },
  metricCardContent: {
    padding: "24px !important",
  },
  metricIcon: {
    fontSize: 48,
    opacity: 0.8,
    marginBottom: 12,
    [theme.breakpoints.down("sm")]: {
      fontSize: 36,
      marginBottom: 8,
    },
  },
  metricValue: {
    fontSize: "2rem",
    fontWeight: 700,
    marginBottom: 8,
    color: "#212121",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.75rem",
    },
  },
  metricLabel: {
    fontSize: "0.875rem",
    color: "#757575",
    marginBottom: 16,
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    marginTop: 12,
    backgroundColor: "#E0E0E0",
  },
  progressBarFilled: {
    backgroundColor: "#4CAF50",
  },
  alertItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
    padding: "8px 12px",
    borderRadius: 6,
    backgroundColor: "#FFF3E0",
  },
  alertIcon: {
    fontSize: 20,
    color: "#F57C00",
  },
  alertText: {
    fontSize: "0.875rem",
    color: "#424242",
    flex: 1,
  },
}));

// ==================== FUNCIONES HELPER ====================

/**
 * Calcula métricas de tareas
 */
const calcularMetricasTareas = (tareasActivas, tareasTerminadas) => {
  const totalActivas = tareasActivas.filter(
    (t) => t.estado === "PENDIENTE" || t.estado === "EN PROCESO",
  ).length;
  const pendientes = tareasActivas.filter(
    (t) => t.estado === "PENDIENTE",
  ).length;
  const enProceso = tareasActivas.filter(
    (t) => t.estado === "EN PROCESO",
  ).length;
  const totalTerminadas = tareasTerminadas.length;
  const totalTareas = totalActivas + totalTerminadas;
  const porcentajeCompletado =
    totalTareas > 0 ? Math.round((totalTerminadas / totalTareas) * 100) : 0;

  return {
    totalActivas,
    pendientes,
    enProceso,
    totalTerminadas,
    porcentajeCompletado,
  };
};

/**
 * Calcula métricas de facturas a partir del listado de impagas.
 * Usa `montoRestante` cuando está disponible (contempla abonos parciales).
 * La condición de "vencida" viene del backend — calculada con el
 * periodo de vencimiento propio del cliente.
 */
const calcularMetricasFacturas = (facturasImpagas) => {
  const sumarMonto = (lista) =>
    lista.reduce(
      (sum, f) => sum + (parseFloat(f.montoRestante ?? f.total) || 0),
      0,
    );

  const facturasAtrasadas = facturasImpagas.filter((f) => f.vencida === 1);

  return {
    totalPendientes: facturasImpagas.length,
    montoPendiente: sumarMonto(facturasImpagas),
    facturasAtrasadas: facturasAtrasadas.length,
    montoAtrasado: sumarMonto(facturasAtrasadas),
  };
};

/**
 * Calcula alertas y acciones pendientes
 */
const calcularAlertas = (tareasActivas, facturasImpagas) => {
  const alertas = [];

  const facturasAtrasadas = facturasImpagas.filter((f) => f.vencida === 1);

  if (facturasAtrasadas.length > 0) {
    const montoTotal = facturasAtrasadas.reduce(
      (sum, f) => sum + (parseFloat(f.montoRestante ?? f.total) || 0),
      0,
    );

    alertas.push({
      tipo: "facturas",
      cantidad: facturasAtrasadas.length,
      descripcion: `${facturasAtrasadas.length} factura${facturasAtrasadas.length > 1 ? "s" : ""} con atraso`,
      valor: montoTotal,
    });
  }

  // Tareas pendientes (más de 5)
  const tareasPendientes = tareasActivas.filter(
    (t) => t.estado === "PENDIENTE",
  ).length;
  if (tareasPendientes > 5) {
    alertas.push({
      tipo: "tareas",
      cantidad: tareasPendientes,
      descripcion: `${tareasPendientes} tareas pendientes`,
    });
  }

  return alertas;
};

export default function DashboardCliente() {
  const { rut, nombre } = useClienteActual();
  const classes = useStyles();

  const [value, setValue] = useState(0);
  const [mostrarFacturas, setMostrarFacturas] = useState(false);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  // Manejar clicks en los cards de métricas
  const handleCardClick = (tipo) => {
    if (tipo === "tareas" && FEATURE_FLAGS.PORTAL_CLIENTE_TAREAS) {
      setValue(0); // Ir al tab de Tareas
    } else if (
      tipo === "tareas-terminadas" &&
      FEATURE_FLAGS.PORTAL_CLIENTE_TAREAS
    ) {
      setValue(1); // Ir al tab de Tareas Terminadas
    } else if (tipo === "facturas") {
      setMostrarFacturas(true); // Mostrar modal de facturas
    } else if (tipo === "alertas") {
      // Con tareas deshabilitadas, las alertas son solo de facturas
      setMostrarFacturas(true);
    }
  };

  // RTK Query - Obtener tareas del cliente
  // Se omite la llamada cuando la feature está deshabilitada
  const { data: tareasCliente = [], isLoading: isLoadingTareas } =
    useObtenerTareasClienteQuery(rut, {
      skip: !FEATURE_FLAGS.PORTAL_CLIENTE_TAREAS,
    });

  // Todas las impagas — alimenta card del dashboard, métricas y contenido
  // del modal cuando el cliente hace click manualmente.
  const { data: facturasImpagas = [], isLoading: isLoadingImpagas } =
    useObtenerFacturasImpagasQuery(rut);

  // Solo vencidas — gatilla el auto-open del modal en la primera visita
  // (flujo de cobranza urgente).
  const { data: facturasPendientes = [], isLoading: isLoadingPendientes } =
    useObtenerFacturasPendientesPagoQuery(rut);

  const isLoading =
    (FEATURE_FLAGS.PORTAL_CLIENTE_TAREAS ? isLoadingTareas : false) ||
    isLoadingImpagas;

  // Memoizar arrays filtrados para evitar re-renders innecesarios
  const tareasActivas = useMemo(
    () => tareasCliente.filter((e) => e.estado !== "TERMINADA"),
    [tareasCliente],
  );

  const tareasTerminadas = useMemo(
    () => tareasCliente.filter((e) => e.estado === "TERMINADA"),
    [tareasCliente],
  );

  // Calcular métricas de tareas
  const metricasTareas = useMemo(
    () => calcularMetricasTareas(tareasActivas, tareasTerminadas),
    [tareasActivas, tareasTerminadas],
  );

  // Calcular métricas de facturas a partir de TODAS las impagas
  const metricasFacturas = useMemo(
    () => calcularMetricasFacturas(facturasImpagas),
    [facturasImpagas],
  );

  // Calcular alertas — cuando las tareas están deshabilitadas, solo se
  // consideran alertas de facturas (vencidas)
  const alertas = useMemo(
    () =>
      calcularAlertas(
        FEATURE_FLAGS.PORTAL_CLIENTE_TAREAS ? tareasActivas : [],
        facturasImpagas,
      ),
    [tareasActivas, facturasImpagas],
  );

  // Auto-open del modal en primera visita: solo si hay facturas VENCIDAS
  // (cobranza urgente). El click manual en el card abre el modal igual,
  // tenga o no vencidas.
  useEffect(() => {
    if (isLoadingPendientes) return;

    const lookBills = localStorage.getItem("lookBills");
    if (lookBills === null) {
      localStorage.setItem("lookBills", JSON.stringify(true));
    }

    if (lookBills !== "false" && facturasPendientes.length >= 1) {
      setMostrarFacturas(true);
    }
  }, [isLoadingPendientes, facturasPendientes.length]);

  return (
    <Page title="TAXTIC">
      <Container>
        <Titulo>
          <Typography variant="h4">Bienvenido(a) {nombre}</Typography>
        </Titulo>

        {/* ==================== SECCIÓN DE MÉTRICAS ==================== */}
        <Box sx={{ mb: 4 }}>
          {isLoading ? (
            <CardSkeleton count={3} md={4} height={220} />
          ) : (
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {/* Card 1: Tareas — oculto mientras PORTAL_CLIENTE_TAREAS esté deshabilitado */}
              {FEATURE_FLAGS.PORTAL_CLIENTE_TAREAS && (
                <Grid item xs={12} sm={6} md={4}>
                  <Card className={classes.metricCard}>
                    <CardActionArea onClick={() => handleCardClick("tareas")}>
                      <CardContent className={classes.metricCardContent}>
                        <AssignmentOutlined
                          className={classes.metricIcon}
                          style={{ color: "#2196F3" }}
                        />
                        <Typography className={classes.metricValue}>
                          {metricasTareas.totalActivas}
                        </Typography>
                        <Typography className={classes.metricLabel}>
                          Tareas Activas
                        </Typography>

                        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                          <StatusBadge
                            estado="PENDIENTE"
                            tipo="tarea"
                            customLabel={`${metricasTareas.pendientes} Pendientes`}
                          />
                          <StatusBadge
                            estado="EN PROCESO"
                            tipo="tarea"
                            customLabel={`${metricasTareas.enProceso} En Proceso`}
                          />
                        </Box>

                        {/* Barra de progreso */}
                        <Box>
                          <Typography
                            variant="caption"
                            style={{ color: "#757575", marginBottom: 4 }}
                          >
                            Progreso general:{" "}
                            {metricasTareas.porcentajeCompletado}%
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={metricasTareas.porcentajeCompletado}
                            className={classes.progressBar}
                            classes={{ bar: classes.progressBarFilled }}
                          />
                          <Typography
                            variant="caption"
                            style={{ color: "#9E9E9E", marginTop: 4 }}
                          >
                            {metricasTareas.totalTerminadas} de{" "}
                            {metricasTareas.totalActivas +
                              metricasTareas.totalTerminadas}{" "}
                            completadas
                          </Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              )}

              {/* Card 2: Facturas — ocupa md=6 cuando tareas está oculto, md=4 cuando está visible */}
              <Grid
                item
                xs={12}
                sm={6}
                md={FEATURE_FLAGS.PORTAL_CLIENTE_TAREAS ? 4 : 6}
              >
                <Card className={classes.metricCard}>
                  <CardActionArea onClick={() => handleCardClick("facturas")}>
                    <CardContent className={classes.metricCardContent}>
                      <ReceiptOutlined
                        className={classes.metricIcon}
                        style={{ color: "#FF9800" }}
                      />
                      <Typography className={classes.metricValue}>
                        {metricasFacturas.totalPendientes}
                      </Typography>
                      <Typography className={classes.metricLabel}>
                        Facturas Pendientes
                      </Typography>

                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="h6"
                          style={{ color: "#424242", fontWeight: 600 }}
                        >
                          {formatAsCLP(metricasFacturas.montoPendiente)}
                        </Typography>
                        <Typography
                          variant="caption"
                          style={{ color: "#757575" }}
                        >
                          Monto total pendiente
                        </Typography>
                      </Box>

                      {metricasFacturas.facturasAtrasadas > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <PaymentBadge estado="PENDIENTE" showAtraso={false} />
                          <Typography
                            variant="caption"
                            style={{
                              color: "#E65100",
                              marginLeft: 8,
                              fontWeight: 500,
                            }}
                          >
                            {metricasFacturas.facturasAtrasadas} con atraso
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>

              {/* Card 3: Alertas/Acciones Pendientes */}
              <Grid
                item
                xs={12}
                sm={6}
                md={FEATURE_FLAGS.PORTAL_CLIENTE_TAREAS ? 4 : 6}
              >
                <Card className={classes.metricCard}>
                  <CardActionArea onClick={() => handleCardClick("alertas")}>
                    <CardContent className={classes.metricCardContent}>
                      <WarningAmberOutlined
                        className={classes.metricIcon}
                        style={{
                          color: alertas.length > 0 ? "#F57C00" : "#9E9E9E",
                        }}
                      />
                      <Typography className={classes.metricValue}>
                        {alertas.length}
                      </Typography>
                      <Typography className={classes.metricLabel}>
                        {alertas.length === 1
                          ? "Alerta Pendiente"
                          : "Alertas Pendientes"}
                      </Typography>

                      {alertas.length === 0 ? (
                        <Box sx={{ mt: 2 }}>
                          <Typography
                            variant="body2"
                            style={{ color: "#4CAF50", fontWeight: 500 }}
                          >
                            ✓ Todo al día
                          </Typography>
                          <Typography
                            variant="caption"
                            style={{ color: "#757575" }}
                          >
                            No hay acciones pendientes
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ mt: 2 }}>
                          {alertas.map((alerta, index) => (
                            <Box key={index} className={classes.alertItem}>
                              <WarningAmberOutlined
                                className={classes.alertIcon}
                              />
                              <Box className={classes.alertText}>
                                <Typography
                                  variant="body2"
                                  style={{ fontWeight: 500 }}
                                >
                                  {alerta.descripcion}
                                </Typography>
                                {alerta.valor && (
                                  <Typography
                                    variant="caption"
                                    style={{ color: "#757575" }}
                                  >
                                    Monto: {formatAsCLP(alerta.valor)}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            </Grid>
          )}
        </Box>

        {/* Tabs de Tareas — ocultos mientras PORTAL_CLIENTE_TAREAS esté deshabilitado */}
        {FEATURE_FLAGS.PORTAL_CLIENTE_TAREAS && (
          <Box sx={{ width: "100%", margin: "10px" }}>
            <Box sx={{ margin: "10px", borderColor: "divider" }}>
              <Tabs
                value={value}
                onChange={handleChange}
                aria-label="basic tabs example"
              >
                <Tab label="Tareas" {...a11yProps(0)} />
                <Tab label="Tareas Terminadas" {...a11yProps(1)} />
              </Tabs>
            </Box>
            <TabPanel value={value} index={0}>
              <TablaInicioCliente tareasCliente={tareasActivas} />
            </TabPanel>
            <TabPanel value={value} index={1}>
              <TareasTerminadas tareasCliente={tareasTerminadas} />
            </TabPanel>
          </Box>
        )}
      </Container>

      {mostrarFacturas && facturasImpagas.length >= 1 && (
        <FacturasPendientesPagoDialog
          open={mostrarFacturas}
          setOpen={setMostrarFacturas}
          facturasPendientes={facturasImpagas}
        />
      )}
    </Page>
  );
}
